import { colors } from '@kmns-internal/furikake'
import {
  AccountTree,
  ChevronRight,
  DeleteOutline,
  ExpandMore,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material'
import {
  ResponseTypeEnum,
  type TemplateNodeSchema,
} from '@ulysses-inc/harami_api_client'
import { type FC, type ReactNode, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { ClampText } from 'src/components/clampText/ClampText'
import { getRelatedExcelConversionFlowInQuestion } from 'src/features/templateEdit/common/getRelatedExcelConversionFlow'
import { DragButtonColor } from 'src/features/theme/KdsThemeColor'
import {
  changeLogicToNext,
  changeQuestionToNext,
  deleteTemplateNode,
  updateTemplateNode,
} from 'src/state/ducks/templates/actions'
import {
  showEditMultipleChoiceLogic,
  showEditNumberLogic,
} from 'src/state/ducks/templates/editLogic/actions'
import type { RootState } from 'src/state/store'
import DeleteConfirmDialog from 'src/views/components/dialogs/DeleteConfirmDialog'
import { useGridLayoutSoftLimitErrorLog } from '../useSoftLimitErrorLog'
import { validateLogicNestCountOnInsertingNewLogic } from '../validates/validate'
import { QuestionProps } from './Question'
import {
  QuestionNameParagraph,
  QuestionNameParagraphWrapper,
  QuestionTableCell,
  QuestionWrapper,
} from './Question.components'
import { useDragAndDrop } from './Question.hooks'
import { checkIsEmployeeCheck } from './Question.utils'
import { IQuestionBodyStates } from './QuestionBody'
import SelectNodeEditor from './SelectNodeEditor'

const HandleIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="24"
    height="24"
    focusable="false"
    aria-hidden="true"
  >
    <path fill="none" d="M0 0h24v24H0V0z"></path>
    <path
      fill={DragButtonColor}
      d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"
    ></path>
  </svg>
)

const QuestionNameAndButtons: FC<
  Pick<QuestionProps, 'node' | 'parentNode' | 'isActive' | 'templateNodes'> &
    Pick<IQuestionBodyStates, 'isOpenState'> &
    Pick<ReturnType<typeof useDragAndDrop>, 'drag' | 'preview'>
> = ({
  node,
  parentNode,
  isActive,
  templateNodes,
  isOpenState: [isOpen, setIsOpen],
  preview,
  drag,
}) => {
  const [errorDialogMessage, setErrorDialogMessage] = useState<string | null>(
    null,
  )

  const excelConversionFlows = useSelector(
    (state: RootState) =>
      state.templatesState.editTemplate.excelConversionFlows,
    shallowEqual,
  )

  const { layoutType } = useSelector(
    (state: RootState) => state.templatesState.editTemplate,
    shallowEqual,
  )

  const { logSoftLimitErrorEvent } = useGridLayoutSoftLimitErrorLog('addNode')

  const dispatch = useDispatch()
  const dispatchChangeQuestionToNext = (name: string) => {
    const n: TemplateNodeSchema = {
      ...node,
      question: { ...node.question, name },
    }

    if (n.nodes?.length) {
      dispatch(changeLogicToNext(node.id, n))
    } else {
      dispatch(changeQuestionToNext(node.id, n, parentNode))
    }
  }
  const dispatchDeleteTemplateNode = () => {
    dispatch(deleteTemplateNode(node.id, parentNode))
  }
  const dispatchShowEditLogic = () => {
    const showErrorModal = (errorMsg: string) => {
      setErrorDialogMessage(errorMsg)
      logSoftLimitErrorEvent('logicNestCount', errorMsg)
    }

    const errorMsg = validateLogicNestCountOnInsertingNewLogic(
      layoutType,
      node,
      templateNodes,
    )

    if (errorMsg) {
      showErrorModal(errorMsg)
      return
    }

    if (node.question?.responseType === ResponseTypeEnum.MULTIPLE_CHOICE) {
      dispatch(showEditMultipleChoiceLogic(node))
      return
    }

    if (node.question?.responseType === ResponseTypeEnum.NUMBER) {
      dispatch(showEditNumberLogic(node))
      return
    }
  }
  const dispatchUpdateTemplateNode = (
    values: Partial<TemplateNodeSchema['question']>,
    nodeId: number = node.id,
  ) => {
    dispatch(
      updateTemplateNode(nodeId, {
        ...node,
        question: { ...node.question, ...values },
      }),
    )
  }

  const questionName = node.question?.name ?? ''
  const isEmployeeCheck = checkIsEmployeeCheck(parentNode, node)
  const isShownDragHandle = !isOpen || node.nodes.length === 0
  const isNumber = node.question?.responseType === ResponseTypeEnum.NUMBER
  const isMultipleChoice =
    node.question?.responseType === ResponseTypeEnum.MULTIPLE_CHOICE
  const isShowBranchesIcon =
    isMultipleChoice ||
    (isNumber && !!(node.question?.responseNumbers ?? [])[0]?.subType)

  const onClickDelete = () => {
    const content: ReactNode[] = []

    if (node.nodes.length) {
      content.push('紐づく質問やセクションも一緒に削除されます。')
    }

    const flowNames = getRelatedExcelConversionFlowInQuestion(
      node.id,
      templateNodes,
      excelConversionFlows,
    )
    if (flowNames.length) {
      if (content.length > 0) {
        content.push(
          <>
            <br />
            <br />
          </>,
        )
      }

      content.push(
        <>
          以下のExcel変換設定に紐づいています。
          <Box
            component="ul"
            sx={{ maxHeight: 220, pl: '20px', pb: '30px', overflowY: 'scroll' }}
          >
            {flowNames.map((name, index) => (
              <li key={index}>{name}</li>
            ))}
          </Box>
        </>,
      )
    }

    DeleteConfirmDialog({
      title: '質問を削除してもよろしいですか？',
      content,
      onOk: () => dispatchDeleteTemplateNode(),
    })
  }

  return (
    <>
      <QuestionTableCell theme={{ width: '100%' }}>
        <QuestionWrapper ref={preview}>
          {!!node.nodes.length && (
            <Stack direction="row" alignItems="center" sx={{ ml: 2 }}>
              <IconButton
                onClick={() => setIsOpen(!isOpen)}
                size="small"
                aria-label={isOpen ? '条件分岐を閉じる' : '条件分岐を開く'}
                sx={{
                  bgcolor: colors.primary.focusVisible,
                  color: colors.action.active,
                  '&:hover': {
                    bgcolor: colors.primary.focusVisible,
                    opacity: 0.9,
                  },
                }}
              >
                {isOpen ? <ExpandMore /> : <ChevronRight />}
              </IconButton>
            </Stack>
          )}
          {isShownDragHandle && !isEmployeeCheck && (
            <Box
              ref={drag}
              sx={{
                ml: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '2rem',
                height: 'auto',
                zIndex: 1,
                outline: 'none',
                cursor: 'grab',
              }}
            >
              <HandleIcon />
            </Box>
          )}
          <QuestionNameParagraphWrapper data-testid="question-name">
            {isActive ? (
              <SelectNodeEditor
                onChangeText={dispatchChangeQuestionToNext}
                onUpdate={name => dispatchUpdateTemplateNode({ name })}
                // どのタイプの質問であっても複数行の入力を許可する
                multiline
                name={questionName}
                node={node}
              />
            ) : (
              <QuestionNameParagraph>
                <ClampText line={2}>{questionName}</ClampText>
              </QuestionNameParagraph>
            )}
          </QuestionNameParagraphWrapper>
          <Stack direction="row" alignItems="center">
            {!isEmployeeCheck && (
              <Tooltip title="質問を削除">
                <IconButton
                  color="error"
                  onClick={onClickDelete}
                  aria-label="質問の削除"
                >
                  <DeleteOutline color="error" />
                </IconButton>
              </Tooltip>
            )}
            {isShowBranchesIcon && (
              <Tooltip title="条件分岐の設定">
                <IconButton
                  color="primary"
                  onClick={dispatchShowEditLogic}
                  aria-label="条件分岐の設定"
                >
                  <AccountTree color="primary" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </QuestionWrapper>
      </QuestionTableCell>
      <Dialog
        open={errorDialogMessage !== null}
        onClose={() => setErrorDialogMessage(null)}
      >
        <DialogTitle>条件分岐の追加ができません</DialogTitle>
        <DialogContent>
          <DialogContentText>{errorDialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialogMessage(null)}>OK</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default QuestionNameAndButtons
